import React, { useCallback, useContext, useMemo } from 'react'

import { RuleType } from '@asta/core'
import { useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import { capitalize } from 'lodash'
import qs from 'qs'
import { Link as RRDLink } from 'react-router-dom'

import type { RunOption } from '@/components/feature/AnalyticsRunFilter'
import { AnalyticsRunFilter } from '@/components/feature/AnalyticsRunFilter'
import SummaryStatistics from '@/components/feature/SummaryStatistics/Icons/SummaryStatisticsIcons'
import { AstaCheckbox } from '@/components/shared/AstaCheckbox'
import { AstaTableV2 } from '@/components/shared/AstaTable/AstaTable'
import type { CustomFilterShape } from '@/components/shared/CustomFilter'
import type { HideColumnFilterOptions } from '@/components/shared/HideColumnFilter'
import { useQueryState } from '@/components/shared/QueryState/useQueryState'
import type {
	Operator,
	OperatorAndValue,
	SelectFilterDefinition,
} from '@/components/shared/TableFilters'
import TableFilters from '@/components/shared/TableFilters'
import ViewsManager from '@/components/shared/ViewsManager'
import { useFormatColumnVisibilityState } from '@/components/shared/ViewsManager/helpers'
import useViews from '@/components/shared/ViewsManager/useViews'
import { AuthContext } from '@/contexts/auth'
import { useSelectedApp } from '@/contexts/selectedApp'
import { DEFAULT_STALE_TIME } from '@/deprecated/services/constants'
import { createRunsAdapter } from '@/hooks/App/useRuns'
import { canEdit, canRemove } from '@/pages/Admin/helpers'
import { useComponentData } from '@/pages/App/Dashboard/hooks'
import { ruleTypeToLabel } from '@/pages/shared/helpers'
import { TEST_CENTER_ROUTES } from '@/routes/routes'
import type { Asset, AssetWithParent, Tag } from '@/shared/types'
import { filter } from '@/shared/utils'

import type {
	AssetTableEntryDTO,
	ResourceDto,
	RuleData,
} from '../../../api/repository/repositorySchemas'
import { AssetActions } from './ActionsCell'
import { useAssetTableData } from './hooks/useAssetTableData'
import { NameCell } from './NameCell'
import { TagsCell } from './TagsCell/TagsCell'
import type { TagWithColor } from './utils'
import { getAvailableTagsObjWithColor } from './utils'

type AssetType = Asset['type']

const EMPTY_ARRAY = []

export interface TestAssetsTableProps {
	type: AssetType
	update: (asset: Asset, tags: Asset['tags']) => void
	rowOnClick: (asset: AssetWithParent) => void
	actions: React.ReactNode[]
	deleteAsset: (asset: AssetWithParent | Array<AssetWithParent>) => void
	tags: TagWithColor[]
	selectedAssets: Partial<Asset>[]
	/** Selected run IDs for multi-run analytics (rules/flows only) */
	analyticsRunIds?: string[]
	/** Available run options for the analytics filter dropdown */
	runFilterOptions?: RunOption[]
	/** Callback when analytics run selection changes */
	onAnalyticsRunIdsChange?: (runIds: string[]) => void
	/** Whether runs are still loading */
	isLoadingRuns?: boolean
}

interface TestAssetsTableFilterShape extends CustomFilterShape {
	type?: {
		[key in '$eq' | '$ne']: string
	}
	tag?: {
		$eq: string
	}
	status?: {
		$eq: string
		$ne: string
	}
	lastTestedRun?: {
		$eq: string
	}
}

interface TestAssetsTableGroupByShape extends OperatorAndValue {
	$eq: string
}

interface SearchFilterShape extends OperatorAndValue {
	$regex: string
}

export interface TestAssetsTableURLParams {
	filters?: TestAssetsTableFilterShape
	groupBy?: TestAssetsTableGroupByShape
	search?: SearchFilterShape
	sort?: string
	editingAssetId?: string
	redirected?: string
	isCreating?: boolean
}

export interface FlowsURLParams {
	search?: Record<Extract<Operator, '$regex'>, string>
	editingAssetId?: string
	filters?: {
		tag?: Partial<Record<Extract<Operator, '$eq' | '$ne'>, string>>
		status?: Partial<
			Record<
				Extract<Operator, '$eq' | '$ne'>,
				'active' | 'inactive' | 'invalid' | 'deleted'
			>
		>
		lastTestedRun?: Partial<Record<Extract<Operator, '$eq'>, string>>
	}
	columnVisibility?: {
		select?: false
		name?: false
		actions?: false
		status?: false
		location?: false
		tags?: false
		lastModified?: false
		lastModifiedBy?: false
	}
	groupBy?: Record<Extract<Operator, '$eq'>, 'tag'>
}

export interface DataURLParams {
	search?: Record<Extract<Operator, '$regex'>, string>
	filters?: {
		tag?: Partial<Record<Extract<Operator, '$eq' | '$ne'>, string>>
		status?: Partial<
			Record<
				Extract<Operator, '$eq' | '$ne'>,
				'active' | 'inactive' | 'invalid' | 'deleted'
			>
		>
	}
	columnVisibility?: {
		select?: false
		name?: false
		actions?: false
		status?: false
		location?: false
		tags?: false
		lastModified?: false
		lastModifiedBy?: false
	}
	groupBy?: Record<Extract<Operator, '$eq'>, 'tag'>
}

export interface FormSpecsURLParams {
	search?: Record<Extract<Operator, '$regex'>, string>
	filters?: {
		tag?: Partial<Record<Extract<Operator, '$eq' | '$ne'>, string>>
		status?: Partial<
			Record<
				Extract<Operator, '$eq' | '$ne'>,
				'active' | 'inactive' | 'invalid' | 'deleted'
			>
		>
	}
	columnVisibility?: {
		select?: false
		name?: false
		actions?: false
		status?: false
		location?: false
		tags?: false
		lastModified?: false
		lastModifiedBy?: false
	}
	groupBy?: Record<Extract<Operator, '$eq'>, 'tag'>
}

export interface RunTemplatesURLParams {
	search?: Record<Extract<Operator, '$regex'>, string>
	editingAssetId?: string
	filters?: {
		tag?: Partial<Record<Extract<Operator, '$eq' | '$ne'>, string>>
		status?: Partial<
			Record<
				Extract<Operator, '$eq' | '$ne'>,
				'active' | 'inactive' | 'invalid' | 'deleted'
			>
		>
	}
	columnVisibility?: {
		select?: false
		name?: false
		actions?: false
		status?: false
		location?: false
		tags?: false
		lastModified?: false
		lastModifiedBy?: false
	}
	groupBy?: Record<Extract<Operator, '$eq'>, 'tag'>
}

export interface RulesURLParams {
	redirected?: boolean
	search?: Record<Extract<Operator, '$regex'>, string>
	editingAssetId?: string
	filters?: {
		tag?: Partial<Record<Extract<Operator, '$eq' | '$ne'>, string>>
		type?: Partial<Record<Extract<Operator, '$eq' | '$ne'>, RuleType>>
		status?: Partial<
			Record<
				Extract<Operator, '$eq' | '$ne'>,
				'active' | 'inactive' | 'invalid' | 'deleted'
			>
		>
		lastTestedRun?: Partial<Record<Extract<Operator, '$eq'>, string>>
	}
	columnVisibility?: {
		select?: false
		name?: false
		actions?: false
		status?: false
		rule_type?: false
		lastResults?: false
		lastTested?: false
		lastRun?: false
		location?: false
		tags?: false
		lastModified?: false
		lastModifiedBy?: false
	}
	groupBy?: Record<Extract<Operator, '$eq'>, 'tag' | 'type'>
}

type ColumnContainerProps = {
	start?: boolean
}

const ColumnContainer: React.FC<
	React.PropsWithChildren<ColumnContainerProps>
> = ({ start, children }) => (
	<div
		className={`w-full h-[26px] flex items-center ${start ? 'justify-start' : 'justify-center'}`}
	>
		{children}
	</div>
)

const StyledRRDLink: React.FC<React.ComponentProps<typeof RRDLink>> = ({
	className = '',
	...props
}) => (
	<RRDLink
		className={`w-fit cursor-pointer text-primary no-underline hover:underline ${className}`}
		{...props}
	/>
)

const columnHelper = createColumnHelper<AssetTableEntryDTO>()

const TestAssetsTable: React.FC<TestAssetsTableProps> = ({
	type,
	update,
	rowOnClick,
	actions,
	deleteAsset,
	tags,
	selectedAssets,
	analyticsRunIds = [],
	runFilterOptions = [],
	onAnalyticsRunIdsChange,
	isLoadingRuns = false,
}) => {
	const { user } = useContext(AuthContext)
	const { selectedApp } = useSelectedApp()
	const { _id: selectedAppId } = selectedApp ?? { _id: '' }

	const queryClient = useQueryClient()
	const RunsAdapter = createRunsAdapter(queryClient)
	const { data: runs } = RunsAdapter.useAll(
		selectedAppId,
		type === 'rule' || type === 'flow'
	)

	const [search, setSearch] = useQueryState<{
		$regex: string
	}>('search')

	const {
		URLFilters,
		filters,
		setFilters,
		setViewFilters,
		URLGroupBy,
		groupBy,
		setGroupBy,
		setViewGroupBy,
		URLSort,
		sort,
		setSort,
		setViewSort,
		URLColumnVisibility,
		columnVisibility,
		setColumnVisibility,
		setViewColumnVisibility,
		URLColumnOrder,
		columnOrder,
		setColumnOrder,
		setViewColumnOrder,
		resetUrlParams,
	} = useViews<TestAssetsTableFilterShape, TestAssetsTableGroupByShape>()

	const { status, ...otherFilters } = filters

	const { data: assetData = EMPTY_ARRAY, isLoading: isLoadingAssetData } =
		useAssetTableData(type)(
			{
				pathParams: {
					appId: selectedAppId,
				},
				//@ts-expect-error we need to stringify the filters
				queryParams: qs.stringify({
					filters: {
						...otherFilters,
						...(status?.$eq
							? { status: { $eq: status.$eq } }
							: status?.$ne
								? { status: { $ne: status.$ne } }
								: {}),
						...(search && { search }),
					},
					groupBy: {
						groupBy,
					},
					...(sort && { sort }),
				}),
			},
			{
				staleTime: DEFAULT_STALE_TIME,
				refetchOnWindowFocus: false,
			}
		)

	// Fetch multi-run analytics for rules/flows
	const shouldFetchAnalytics =
		!!selectedAppId &&
		analyticsRunIds.length > 0 &&
		(type === 'rule' || type === 'flow')

	const ruleAnalyticsParams = useMemo(
		() => ({
			appId: selectedAppId,
			filter: {
				// Normalize runId: string for single run, array for multiple
				// This matches the pattern used in ModelsPage for consistent API behavior
				runId:
					analyticsRunIds.length === 1
						? analyticsRunIds[0]
						: analyticsRunIds,
			},
			groupBy: ['ruleType', 'ruleId'],
			populate: ['ruleId'],
			enabled: shouldFetchAnalytics,
		}),
		[selectedAppId, analyticsRunIds, shouldFetchAnalytics]
	)

	const {
		data: ruleAnalyticsResponse,
		isLoading: isLoadingRuleAnalytics,
		isFetching: isFetchingRuleAnalytics,
	} = useComponentData(ruleAnalyticsParams)

	// Build a map of ruleId -> { passed, failed } for quick lookup
	const ruleAnalyticsMap = useMemo(() => {
		const analyticsData = ruleAnalyticsResponse?.analytics ?? []
		const map: Record<string, { passed: number; failed: number }> = {}

		for (const entry of analyticsData) {
			const ruleId =
				typeof entry.ruleId === 'object' && entry.ruleId !== null
					? (entry.ruleId as { _id: string })._id
					: (entry.ruleId as string)

			if (!ruleId) continue

			if (!map[ruleId]) {
				map[ruleId] = { passed: 0, failed: 0 }
			}
			map[ruleId].passed += entry.passed ?? 0
			map[ruleId].failed += entry.failed ?? 0
		}

		return map
	}, [ruleAnalyticsResponse])

	// Merge analytics into asset data to ensure table re-renders when analytics change
	// This creates new row objects when analytics data changes, which TanStack Table detects
	const dataWithAnalytics = useMemo(() => {
		if (type !== 'rule' && type !== 'flow') {
			return assetData
		}

		return assetData.map(row => ({
			...row,
			// Embed multi-run analytics directly into the row for re-render detection
			multiRunAnalytics: row.asset?._id
				? ruleAnalyticsMap[row.asset._id]
				: undefined,
		}))
	}, [assetData, ruleAnalyticsMap, type])

	const [availableTagsObj] = useMemo(
		() => [getAvailableTagsObjWithColor(tags)],
		[tags]
	)

	const updateTags = useCallback(
		(asset: Asset) => (tags: string[]) => {
			update(
				{
					...asset,
					tags: tags,
				},
				tags
			)
		},
		[update]
	)

	const onStatusChange = useCallback(
		(asset: AssetWithParent | Array<AssetWithParent>) => {
			const getStatus = (status: Asset['status']) => {
				return status === 'active' ? 'inactive' : 'active'
			}

			const formatTags = (tags: Asset['tags']) => {
				return (tags as Tag[]).map(tag => tag._id)
			}

			if (Array.isArray(asset)) {
				asset.forEach(asset => {
					update(
						{
							...asset,
							status: getStatus(asset.status),
						},
						formatTags(asset.tags)
					)
				})
				return
			}

			update(
				{
					...asset,
					status: getStatus(asset.status),
				},
				formatTags(asset.tags)
			)
		},
		[update]
	)

	const onMultipleAssetDelete = useCallback(
		(assets: Array<AssetWithParent>) => {
			if (type === 'flow') {
				deleteAsset(assets)
				return
			}

			assets.forEach(asset => {
				deleteAsset(asset)
			})
		},
		[deleteAsset, type]
	)

	const columns = useMemo(
		() => [
			columnHelper.accessor(row => row.asset._id, {
				id: 'select',
				minSize: 36,
				size: 36,
				enableSorting: false,
				header: ({ table }) => (
					<ColumnContainer>
						<AstaCheckbox
							checked={table.getIsAllRowsSelected()}
							indeterminate={
								table.getIsSomeRowsSelected() &&
								!table.getIsAllRowsSelected()
							}
							onChange={table.getToggleAllRowsSelectedHandler()}
							aria-label="Select all"
						/>
					</ColumnContainer>
				),
				cell: ({ row }) => (
					<ColumnContainer>
						<AstaCheckbox
							checked={row.getIsSelected()}
							disabled={!row.getCanSelect()}
							indeterminate={row.getIsSomeSelected()}
							onChange={row.getToggleSelectedHandler()}
							aria-label="Select row"
						/>
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.asset.name, {
				id: 'name',
				header: 'Name',
				minSize: 56,
				/**
				 * If the cell is a 'grouping row', which isn't really a grouping row
				 * but is kind of hacked in, because table doesn't support grouping
				 * very well IMO.
				 */
				cell: ({ row }) => (
					<ColumnContainer start>
						<NameCell
							isGroupingActive={!!groupBy?.$eq}
							row={row}
							rowOnClick={rowOnClick}
						/>
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.asset, {
				id: 'actions',
				header: 'Actions',
				minSize: 74,
				size: 74,
				enableSorting: false,
				cell: ({ row }) => (
					<ColumnContainer start>
						<AssetActions
							row={row}
							user={user}
							type={type}
							rowOnClick={rowOnClick}
							onStatusChange={onStatusChange}
							deleteAsset={deleteAsset}
						/>
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.asset.status, {
				id: 'status',
				header: 'Status',
				minSize: 66,
				cell: ({ row }) => {
					const { isGroup, asset } = row.original

					if (isGroup) return null

					return (
						<ColumnContainer start>{asset.status}</ColumnContainer>
					)
				},
			}),
			...(['rule', 'flow'].includes(type)
				? [
						...(type === 'rule'
							? [
									columnHelper.accessor(row => row.asset, {
										id: 'rule_type',
										header: 'Type',
										minSize: 52,
										cell: ({ row }) => {
											const { isGroup, asset } =
												row.original

											if (isGroup) return null

											return (
												<ColumnContainer start>
													{capitalize(
														(asset.data as RuleData)
															.type
													).replace('-', ' ')}
												</ColumnContainer>
											)
										},
									}),
								]
							: []),
						columnHelper.accessor(
							row =>
								(
									row as typeof row & {
										multiRunAnalytics?: {
											passed: number
											failed: number
										}
									}
								).multiRunAnalytics,
							{
								id: 'lastResults',
								header: 'Results',
								minSize: 114,
								enableSorting: false,
								cell: ({ row }) => {
									const { isGroup } = row.original

									if (isGroup) return null

									// Access analytics directly from row data (embedded in dataWithAnalytics)
									// This ensures the cell re-renders when analytics change
									const multiRunStats = (
										row.original as typeof row.original & {
											multiRunAnalytics?: {
												passed: number
												failed: number
											}
										}
									).multiRunAnalytics
									if (!multiRunStats) return null

									return (
										<ColumnContainer>
											<SummaryStatistics
												summaryStatistics={
													multiRunStats
												}
											/>
										</ColumnContainer>
									)
								},
							}
						),
						columnHelper.accessor(
							row => row.analytics.lastTestedTimestamp,
							{
								id: 'lastTested',
								header: 'Last used',
								minSize: 106,
								enableSorting: false,
								cell: ({ row }) => {
									const { isGroup, analytics } = row.original

									if (isGroup || !analytics) return null

									return (
										<ColumnContainer start>
											{new Date(
												row.original.analytics.lastTestedTimestamp
											).toLocaleString()}
										</ColumnContainer>
									)
								},
							}
						),
						columnHelper.accessor(
							row => row.analytics.lastTestedRunId,
							{
								id: 'lastRun',
								header: 'Last used run',
								minSize: 138,
								enableSorting: false,
								cell: ({ row }) => {
									const { isGroup, analytics } = row.original

									if (isGroup || !analytics) return null

									return (
										<ColumnContainer>
											<StyledRRDLink
												to={`/app/${selectedApp?._id}/${TEST_CENTER_ROUTES.log.path(
													{
														run: {
															$eq: row.original
																.analytics
																.lastTestedRunId,
														},
														filters: {
															...(type === 'rule'
																? filter(
																		'ruleId',
																		'$eq',
																		row
																			.original
																			.asset
																			._id
																	)
																: filter(
																		'flowId',
																		'$eq',
																		row
																			.original
																			.asset
																			._id
																	)),
														},
														redirected: true,
													}
												)}`}
											>
												{
													row.original.analytics
														.lastTestedRunNumber
												}
											</StyledRRDLink>
										</ColumnContainer>
									)
								},
							}
						),
					]
				: []),

			columnHelper.accessor(() => 'location', {
				id: 'location',
				header: 'Location',
				minSize: 84,
				cell: ({ row }) => (
					<ColumnContainer start>
						{!row.original.isGroup &&
							row.original.asset.parent.name}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.asset.tags, {
				id: 'tags',
				header: 'Tags',
				minSize: 52,
				enableSorting: false,
				cell: ({ row }) => {
					const { isGroup, asset } = row.original

					if (isGroup) return null

					return (
						<TagsCell
							availableTags={availableTagsObj}
							currentTags={asset.tags as Tag[]}
							updateTags={updateTags(asset)}
							disabled={
								!canEdit(asset.parent.role) && !user.isAdmin
							}
						/>
					)
				},
			}),
			columnHelper.accessor(
				row => new Date(row.asset.updatedAt).getTime(),
				{
					id: 'lastModified',
					header: 'Last Modified',
					minSize: 118,
					cell: ({ row }) => {
						const { isGroup } = row.original

						if (isGroup) return null

						return (
							<ColumnContainer start>
								{new Date(
									row.original.asset.updatedAt
								).toLocaleString()}
							</ColumnContainer>
						)
					},
				}
			),
			columnHelper.accessor(() => 'lastModifiedBy', {
				id: 'lastModifiedBy',
				header: 'Last Modified By',
				minSize: 140,
				cell: ({ row }) => {
					const { isGroup } = row.original

					if (isGroup) return null

					return (
						<ColumnContainer start>
							{row.original.asset.updatedBy}
						</ColumnContainer>
					)
				},
			}),
		],
		[
			availableTagsObj,
			onStatusChange,
			groupBy,
			type,
			deleteAsset,
			rowOnClick,
			user,
			updateTags,
			selectedApp,
		]
	)

	const columnFilterOptions = useMemo(
		() =>
			columns.map(col => ({
				label:
					typeof col.header === 'string'
						? col.header
						: capitalize(col.id),
				value: col.id,
			})) as HideColumnFilterOptions[],
		[columns]
	)

	/**
	 * We check permissions against selected assets, if all of the selected assets don't match the required permissions, we don't show the option
	 */
	const contextMenuOptions = useMemo(
		() =>
			[
				(user.isAdmin ||
					selectedAssets.every(asset =>
						canRemove((asset.parent as ResourceDto).role)
					)) && {
					label: `Delete`,
					onClick: rows =>
						onMultipleAssetDelete(
							rows
								.filter(row => !row.isGroup)
								.map(({ asset }) => asset)
						),
				},
				(user.isAdmin ||
					selectedAssets.every(asset =>
						canEdit((asset.parent as ResourceDto).role)
					)) && {
					label: `Activate / Deactivate`,
					onClick: rows =>
						onStatusChange(
							rows
								.filter(row => !row.isGroup)
								.map(({ asset }) => asset)
						),
				},
			].filter(Boolean),
		[selectedAssets, user, onStatusChange, onMultipleAssetDelete]
	)

	const groupByOptions = useMemo(
		() => [
			{ label: 'Tag', value: 'tag' },
			{ label: 'Status', value: 'status' },
			...(type === ('rule' as AssetType)
				? [{ label: 'Type', value: 'type' }]
				: []),
		],
		[type]
	)

	const tagFilterOptions = useMemo(
		() =>
			tags.map(tag => ({
				label: tag.name,
				value: tag._id,
			})),
		[tags]
	)

	const columnVisibilityState = useFormatColumnVisibilityState(
		columns,
		columnVisibility
	)

	const lastTestedRunFilterOptions = useMemo(
		() =>
			runs
				?.sort((a, b) => Number(b.runNumber) - Number(a.runNumber))
				.map(run => ({
					label: run.runNumber.toString(),
					value: run.runNumber.toString(),
				})),
		[runs]
	)

	const tableFilters = useMemo(
		() => [
			{
				type: 'search' as const,
				key: 'search',
				onChange: (newValues: OperatorAndValue) => {
					setSearch(newValues)
				},
				value: search as OperatorAndValue,
			},
			{
				type: 'custom' as const,
				key: 'filters',
				onChange: setFilters,
				value: filters,
				options: [
					{
						column: { label: 'Status', value: 'status' },
						values: [
							{ label: 'active', value: 'active' },
							{ label: 'inactive', value: 'inactive' },
							{ value: 'invalid', label: 'invalid' },
							{ value: 'deleted', label: 'deleted' },
						],
						type: 'select' as const,
					},
					...(type === ('rule' as AssetType)
						? [
								{
									column: {
										label: 'Type',
										value: 'type',
									},
									values: Object.values(RuleType).map(
										ruleType => ({
											label: ruleTypeToLabel[ruleType],
											value: ruleType,
										})
									),
									type: 'select',
								} as SelectFilterDefinition,
							]
						: []),
					...(tags?.length > 0
						? [
								{
									column: {
										label: 'Tag',
										value: 'tag',
									},
									values: tagFilterOptions,
									type: 'select' as const,
								},
							]
						: []),
					//Only rules and flows have analytics
					...(['rule', 'flow'].includes(type)
						? [
								{
									column: {
										label: 'Last Used Run',
										value: 'lastTestedRun',
									},
									values: lastTestedRunFilterOptions,
									type: 'select' as const,
								},
							]
						: []),
				],
			},
			{
				type: 'group' as const,
				key: 'groupBy',
				options: groupByOptions,
				value: groupBy,
				onChange: setGroupBy,
			},
			{
				type: 'hide-columns' as const,
				key: 'hide columns',
				onChange: setColumnVisibility,
				value: columnVisibilityState,
				options: columnFilterOptions,
			},
		],
		[
			search,
			setSearch,
			filters,
			setFilters,
			type,
			tags,
			tagFilterOptions,
			groupByOptions,
			groupBy,
			setGroupBy,
			columnVisibilityState,
			setColumnVisibility,
			columnFilterOptions,
			lastTestedRunFilterOptions,
		]
	)

	return (
		<div className="h-[calc(100dvh-var(--header-height))] md:w-[calc(100dvw-7rem)] w-screen overflow-hidden flex flex-col">
			<TableFilters filters={tableFilters} actions={actions}>
				{(type === 'rule' || type === 'flow') &&
					onAnalyticsRunIdsChange && (
						<AnalyticsRunFilter
							runs={runFilterOptions}
							selectedRunIds={analyticsRunIds}
							onChange={onAnalyticsRunIdsChange}
							disabled={
								isLoadingRuns || runFilterOptions.length === 0
							}
							label="Analytics"
						/>
					)}
				<ViewsManager
					filters={URLFilters as CustomFilterShape}
					setViewFilters={setViewFilters}
					setViewColumnVisibility={setViewColumnVisibility}
					columnVisibility={URLColumnVisibility}
					groupBy={URLGroupBy}
					setViewGroupBy={setViewGroupBy}
					sort={URLSort}
					setViewSort={setViewSort}
					columnOrder={URLColumnOrder}
					setViewColumnOrder={setViewColumnOrder}
					resetUrlParams={resetUrlParams}
				/>
			</TableFilters>
			<div className="overflow-auto flex-1 bg-accent/30 dark:bg-accent/10">
				<div className="border-t border-b border-r w-fit">
					<AstaTableV2
						key={type}
						columns={columns}
						data={dataWithAnalytics}
						actions={actions}
						columnVisibility={columnVisibilityState}
						setColumnVisibility={setColumnVisibility}
						columnOrder={columnOrder}
						setColumnOrder={setColumnOrder}
						isLoading={isLoadingAssetData}
						sort={sort}
						setSort={setSort}
						contextMenuOptions={contextMenuOptions}
					/>
				</div>
			</div>
		</div>
	)
}

export default TestAssetsTable
